import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Message } from "semantic-ui-react";
import { Link } from "react-router-dom";
import agent from "../../../../app/api/agent";
import { NonAuthRoutes } from "../../../../app/auth/routes";
import { PASSWORD_STRENGTH_REGEXP } from "../../../../app/helpers/regexp";
import AppForm from "../../../../components/form/Form";
import { useAppForm } from "../../../../components/form/useForm";
import { useNavigate } from "react-router-dom";
import useError from "../../../../app/hooks/useError";

interface FormData {
  email: string;
  password: string;
  repeatedPassword: string;
}

interface Props {
  token: string;
  email: string;
}

const PasswordRecoveryConfirmationForm: React.FC<Props> = ({ token, email }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getApiErrorContent } = useError();
  const formHook = useAppForm<FormData>({ defaultValues: { email, password: "", repeatedPassword: "" } });
  const [error, setError] = useState<string | undefined>(undefined);
  const [isInvalidToken, setIsInvalidToken] = useState(false);

  useEffect(() => {
    if (!token) navigate("/");
  }, [navigate, token]);

  const onSubmit = async (data: FormData) => {
    agent.Users.passwordResetConfirm(data.email, data.password, token)
      .then(() => {
        navigate(`${NonAuthRoutes.login}?passwordChanged=true`);
      })
      .catch((err) => {
        setIsInvalidToken(err?.response?.data === "Invalid token");
        setError(getApiErrorContent(err));
      });
  };

  const passwordMatches = (repeatedPassword: string) => {
    return formHook.watch("password") === repeatedPassword ? true : t("auth.steps.password.validation.password_match");
  };

  const renderError = () => {
    if (!error) {
      return null;
    }

    return (
      <Message negative>
        {error}{" "}
        {isInvalidToken && <Link to={NonAuthRoutes.passwordRecovery}>{t("auth.password_reset.reset_link")}</Link>}
      </Message>
    );
  };

  return (
    <Fragment>
      {renderError()}
      <AppForm formHook={formHook} onSubmit={onSubmit}>
        <AppForm.Group name="email" label="common.email">
          <AppForm.Email name="email" rules={{ required: true }} readOnly={!!email} />
        </AppForm.Group>
        <AppForm.Group name="password" label="auth.password_reset_confirm.new_password">
          <AppForm.Password
            name="password"
            autoComplete="new-password"
            rules={{
              required: true,
              pattern: {
                value: PASSWORD_STRENGTH_REGEXP,
                message: t("auth.steps.password.validation.password_strenght"),
              },
            }}
          />
        </AppForm.Group>
        <AppForm.Group name="repeatedPassword" label="auth.steps.password.confirm_password">
          <AppForm.Password name="repeatedPassword" rules={{ required: true, validate: passwordMatches }} />
        </AppForm.Group>
      </AppForm>
      <Button primary onClick={formHook.handleSubmit(onSubmit)}>
        {t("auth.password_reset.form.confirm")}
      </Button>
    </Fragment>
  );
};

export default PasswordRecoveryConfirmationForm;
